import React, { useState } from 'react';
import { SectionWrapper } from './ui/section-wrapper';
import { SectionHeading } from './ui/section-heading';
import { Card } from './ui/card';
import { IconBox } from './ui/icon-box';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select } from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { consultingService } from '../lib/consulting';

const darkBg = { background: 'linear-gradient(160deg, #07000d 0%, #0f0118 40%, #0a000f 100%)' };

// ─── What We Do ───────────────────────────────────────────────────────────────

const services = [
  {
    title: 'AI Strategy and Roadmap',
    desc: 'We help you identify where AI fits in your business and create a clear, prioritised implementation plan so you know exactly where to start.',
    icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  },
  {
    title: 'Workflow Automation',
    desc: 'We design and build automated workflows using tools like Lovable, Claude Code, OpenAI Assistants SDK, and Vercel AI SDK, turning manual tasks into hands-free systems.',
    icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  },
  {
    title: 'AI-Powered Content Systems',
    desc: 'We set up content creation pipelines that match your brand voice, so you can produce consistent, high-quality content at scale without burning out.',
    icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  },
  {
    title: 'Custom AI Solutions',
    desc: 'We build tailored AI tools and integrations for your specific business needs when off-the-shelf products are not quite the right fit.',
    icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  },
  {
    title: 'AI Skills Training',
    desc: 'We train your team to use AI tools effectively through hands-on workshops designed around how your business actually operates day to day.',
    icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  },
  {
    title: 'Tool Selection and Setup',
    desc: 'We evaluate, select, and configure the right AI tools for your stack so you are not paying for tools you do not need or missing ones that would change everything.',
    icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  },
];

// ─── How We Work ─────────────────────────────────────────────────────────────

const steps = [
  {
    n: '01',
    title: 'Discovery Call',
    desc: 'We spend 30 minutes understanding your business, your goals, and your current tech stack. No preparation needed on your end.',
  },
  {
    n: '02',
    title: 'AI Audit',
    desc: 'We analyse your workflows and identify the highest-impact opportunities for AI in your operations, ranked by effort and return.',
  },
  {
    n: '03',
    title: 'Implementation',
    desc: 'We build and deploy AI solutions tailored to your business, working iteratively so you see progress at every stage.',
  },
  {
    n: '04',
    title: 'Training and Handoff',
    desc: 'We train your team and make sure everything runs smoothly before we hand over the keys, with documentation included.',
  },
];

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const faqItems = [
  {
    question: 'What size businesses do you work with?',
    answer: 'We primarily work with small businesses and solopreneurs, typically with teams of 1 to 20 people. If you are a creator, coach, consultant, or agency owner looking to use AI to do more with less, we are probably a good fit.',
  },
  {
    question: 'Do I need a technical background to work with you?',
    answer: 'Not at all. We translate everything into plain language and handle the technical side end to end. Your job is to tell us how your business works and what outcomes you want.',
  },
  {
    question: 'How long does a typical engagement take?',
    answer: 'It depends on the scope. A focused workflow automation project can be done in 2 to 4 weeks. A full AI strategy and implementation engagement typically runs 6 to 12 weeks. We will give you a clear timeline after the discovery call.',
  },
  {
    question: 'What tools and platforms do you work with?',
    answer: 'We work with most major AI platforms and SDKs including OpenAI, Anthropic, Lovable, Claude Code, Vercel AI SDK, Google text embeddings, and more. If you have a preferred stack, we will work within it.',
  },
  {
    question: 'How much does it cost?',
    answer: 'Pricing varies based on the scope and type of engagement. We offer project-based pricing and monthly retainers. Send us a message through the form below and we will put together a proposal tailored to your needs.',
  },
  {
    question: 'What if something breaks after you hand it off?',
    answer: 'Everything we build includes documentation and handoff training. For clients on a retainer, we provide ongoing support and monitoring. For project-based work, we include a 30-day support window after delivery.',
  },
];

// ─── Business types ───────────────────────────────────────────────────────────

const businessTypes = [
  { value: 'coach', label: 'Coach' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'creator', label: 'Creator' },
  { value: 'agency', label: 'Agency' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'other', label: 'Other' },
];

// ─── Contact Form ─────────────────────────────────────────────────────────────

const ConsultingContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business_name: '',
    business_type: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.business_type || !formData.message) return;
    setStatus('loading');
    try {
      const result = await consultingService.submit(formData);
      setStatus(result.success ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <Card variant="light" padding="lg" className="max-w-lg mx-auto text-center shadow-lg">
        <IconBox variant="light" size="xl" className="mx-auto mb-5">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </IconBox>
        <h3 className="text-xl font-bold mb-2 text-gray-900">Message received</h3>
        <p className="text-gray-500 text-sm leading-relaxed">
          Thanks for reaching out. We will review your inquiry and get back to you within 1 to 2 business days.
        </p>
      </Card>
    );
  }

  return (
    <Card variant="light" padding="lg" className="max-w-lg mx-auto shadow-lg">
      <h3 className="text-xl font-bold mb-1 text-center text-gray-900">Tell us about your business</h3>
      <p className="text-sm text-center mb-8 text-gray-500">We will get back to you within 1 to 2 business days.</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label variant="light">Name</Label>
          <Input
            variant="light"
            type="text"
            required
            value={formData.name}
            onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            placeholder="Your name"
          />
        </div>
        <div>
          <Label variant="light">Email</Label>
          <Input
            variant="light"
            type="email"
            required
            value={formData.email}
            onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
            placeholder="you@business.com"
          />
        </div>
        <div>
          <Label variant="light">Business Name</Label>
          <Input
            variant="light"
            type="text"
            value={formData.business_name}
            onChange={e => setFormData(p => ({ ...p, business_name: e.target.value }))}
            placeholder="Your business name (optional)"
          />
        </div>
        <div>
          <Label variant="light">Business Type</Label>
          <Select
            variant="light"
            required
            value={formData.business_type}
            onChange={e => setFormData(p => ({ ...p, business_type: e.target.value }))}
          >
            <option value="" disabled>Select your business type</option>
            {businessTypes.map(bt => (
              <option key={bt.value} value={bt.value}>{bt.label}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label variant="light">What do you need help with?</Label>
          <Textarea
            variant="light"
            required
            value={formData.message}
            rows={4}
            onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
            placeholder="Describe your biggest challenge or what you want to achieve with AI..."
          />
        </div>
        <Button
          type="submit"
          disabled={status === 'loading'}
          className="w-full rounded-xl h-12 text-[15px] font-semibold"
        >
          {status === 'loading' ? 'Sending...' : 'Send Inquiry'}
        </Button>
        {status === 'error' && (
          <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>
        )}
      </form>
    </Card>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ConsultingPage: React.FC = () => {
  const scrollToForm = () =>
    document.getElementById('consulting-contact-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div>
      {/* Hero — dark */}
      <div style={darkBg}>
        <SectionWrapper className="pt-36 pb-20">
          <div className="text-center">
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-purple-400 mb-10">
              AI Consulting
            </p>
            <h1 className="text-4xl lg:text-[4.25rem] font-bold text-white tracking-[-0.03em] mb-6 leading-[1.08]">
              Transform your business<br />
              <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-300 bg-clip-text text-transparent">
                with AI that works
              </span>
            </h1>
            <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              We work with small businesses to identify, implement, and automate AI solutions that save time, reduce costs, and create real competitive advantage.
            </p>
            <Button
              size="lg"
              onClick={scrollToForm}
              className="rounded-full font-semibold shadow-2xl shadow-purple-600/20"
            >
              Work With Us
            </Button>
          </div>
        </SectionWrapper>
      </div>

      {/* What We Do — light */}
      {/*
        6-item grid: 3 cols on lg, 2 cols on sm, 1 col on mobile.
        Connected border pattern: single outer border, inner dividers only.
        Row 0: items 0,1,2 — Row 1: items 3,4,5
        border-r: not the last col per breakpoint
        border-b: top row (i < 3 for lg, every pair except last for sm)
      */}
      <div className="bg-white">
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading
            label="What We Do"
            title="How we help your business grow"
            description="Six focused areas where we help small businesses unlock real value from AI."
            variant="light"
          />
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service, i) => {
                // Compute divider classes for each breakpoint independently.
                // Default (1-col): border-b for all except last item
                const borderBDefault = i < services.length - 1 ? 'border-b border-gray-200' : '';
                // sm (2-col): right border for left-column items; bottom border except last row
                const isRightColSm = i % 2 === 1;
                const isLastRowSm = i >= services.length - 2; // last 2 items
                const borderRSm = !isRightColSm ? 'sm:border-r sm:border-gray-200' : 'sm:border-r-0';
                const borderBSm = !isLastRowSm ? 'sm:border-b sm:border-gray-200' : 'sm:border-b-0';
                // lg (3-col): right border except 3rd col; bottom border only top row (i < 3)
                const isRightColLg = i % 3 === 2;
                const isLastRowLg = i >= 3; // items 3-5 are the last row in 3-col
                const borderRLg = !isRightColLg ? 'lg:border-r lg:border-gray-200' : 'lg:border-r-0';
                const borderBLg = !isLastRowLg ? 'lg:border-b lg:border-gray-200' : 'lg:border-b-0';

                return (
                  <div
                    key={i}
                    className={`p-6 lg:p-8 ${borderBDefault} ${borderRSm} ${borderBSm} ${borderRLg} ${borderBLg}`}
                  >
                    <IconBox variant="light" size="lg" className="mb-5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={service.icon} />
                      </svg>
                    </IconBox>
                    <h3 className="text-gray-900 font-bold text-base mb-2">{service.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{service.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionWrapper>
      </div>

      {/* How We Work — dark */}
      {/*
        4-item grid: 4 cols on lg, 2 cols on sm, 1 col on mobile.
        border-r: not the last col per breakpoint
        border-b: items in non-last rows
      */}
      <div style={darkBg}>
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading
            label="How We Work"
            title="A clear process, start to finish"
            description="Four straightforward steps from first conversation to a running AI system in your business."
            variant="dark"
          />
          <div className="border border-white/[0.08] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((step, i) => {
                // Default (1-col): border-b except last
                const borderBDefault = i < steps.length - 1 ? 'border-b border-white/[0.08]' : '';
                // sm (2-col): right for left col; bottom for top row (i < 2)
                const isRightColSm = i % 2 === 1;
                const isLastRowSm = i >= 2;
                const borderRSm = !isRightColSm ? 'sm:border-r sm:border-white/[0.08]' : 'sm:border-r-0';
                const borderBSm = !isLastRowSm ? 'sm:border-b sm:border-white/[0.08]' : 'sm:border-b-0';
                // lg (4-col): right except last col; no bottom border (single row)
                const isLastColLg = i === 3;
                const borderRLg = !isLastColLg ? 'lg:border-r lg:border-white/[0.08]' : 'lg:border-r-0';
                const borderBLg = 'lg:border-b-0';

                return (
                  <div
                    key={i}
                    className={`p-6 lg:p-8 ${borderBDefault} ${borderRSm} ${borderBSm} ${borderRLg} ${borderBLg}`}
                  >
                    <Badge variant="default" size="sm" className="mb-5 opacity-80">{step.n}</Badge>
                    <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionWrapper>
      </div>

      {/* Contact Form — light */}
      <div className="bg-[#faf8fc]" id="consulting-contact-form">
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading
            label="Get In Touch"
            title="Ready to bring AI into your business?"
            description="Fill in the form below and we will get back to you within 1 to 2 business days."
            variant="light"
            className="mb-14"
          />
          <ConsultingContactForm />
        </SectionWrapper>
      </div>

      {/* FAQ — dark */}
      <div style={darkBg}>
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading
            title="Frequently asked questions"
            variant="dark"
          />
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible>
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} variant="dark" className="mb-2">
                  <AccordionTrigger className="text-white font-medium text-base">
                    <span className="flex-1">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/40 text-sm leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </SectionWrapper>
      </div>
    </div>
  );
};

export default ConsultingPage;
